import { createDisplayName } from '@withjoy/joykit/utils';
import { mergeOverrides } from '@shared/utils/overrides';
import React from 'react';
import type { PopoverV2Props } from '../PopoverV2';
import { PopoverV2 } from '../PopoverV2';
import { Backdrop } from '../Backdrop';
import { Portal } from '../Portal';

const TOOLTIP_OVERRIDES: PopoverV2Props['overrides'] = {
  Content: { props: { paddingX: 5, paddingY: 4, backgroundColor: 'black', color: 'white' } },
  Arrow: { props: { backgroundColor: 'black' } },
};

const defaultProps = {
  triggerType: 'hover',
  placement: 'bottom',
  showArrow: true,
  showBackdrop: false,
} as const;

export type TooltipV2Props = Readonly<
  PopoverV2Props & {
    content: React.ReactNode;
    showBackdrop?: boolean;
    /** When true, renders tooltip content in a portal to escape overflow clipping */
    usePortal?: boolean;
  }
>;

export const TooltipV2 = (props: TooltipV2Props) => {
  const { children: childrenProp, content, overrides: overridesProp, showBackdrop = false, usePortal = false, ...restProps } = props;
  const overrides = mergeOverrides(TOOLTIP_OVERRIDES, overridesProp);

  // Ensure only one child
  const children = React.Children.only(childrenProp);

  const shouldPortalContent = showBackdrop || usePortal;

  return (
    <>
      {showBackdrop && (
        <Portal appendToParentPortal>
          <Backdrop
            isOpen={!!restProps.isOpen}
            zIndex="modal"
          />
        </Portal>
      )}
      <PopoverV2
        disableDisclosureAria
        {...props}
        overrides={overrides}
      >
        <PopoverV2.Trigger>{children}</PopoverV2.Trigger>
        {shouldPortalContent ? (
          <Portal appendToParentPortal>
            <PopoverV2.Content>{content}</PopoverV2.Content>
          </Portal>
        ) : (
          <PopoverV2.Content>{content}</PopoverV2.Content>
        )}
      </PopoverV2>
    </>
  );
};

TooltipV2.displayName = createDisplayName('Tooltip');
TooltipV2.defaultProps = defaultProps;
